import EditableInput from '@/components/editable-input.tsx'
import {
  Menu,
  MenuItem,
  MenuSeparator,
} from '@/components/interactives/dropdown-menu.tsx'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/interactives/Tooltip.jsx'
import {
  useClearRundownPrivateColumn,
  useDeleteRundownColumn,
  useIsPrivateNotesColumn,
} from '@/entities/rundown/index.ts'
import {
  useColumnLayoutActions,
  useRawColumnLayout,
} from '@/shared/store/column-layout.store.ts'
import { toastAtom } from '@/store/toast.store.js'
import {
  faArrowLeft,
  faArrowRight,
  faArrowsLeftRight,
  faCircleInfo,
  faCode,
  faCopy,
  faEllipsis,
  faEyeSlash,
  faPlus,
  faTableColumns,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { Column } from '@rundown-studio/types'
import { RundownPermission } from '@rundown-studio/types'
import copy from 'copy-to-clipboard'
import { useSetAtom } from 'jotai'
import _clamp from 'lodash/clamp'
import { lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { DEFAULT_COLUMN_WIDTH } from '../../../constants/column-width.ts'
import { addRundownColumn, updateRundownColumn } from '../../../firestore.js'
import { dragStartInterceptor } from '../../../utils/dragStartInterceptor.js'
import { useModal } from '../../modal-manager/index.ts'
import { useRundownPermission } from '../../permissions/lib/use-rundown-permission.ts'

const DeleteColumnModal = lazy(
  () => import('../../delete-column-modal/delete-column-modal.tsx'),
)

type Props = {
  column: Column
  moveColumn: () => void
  updateWidth: (columnId: string, width: number) => void
  onDragStart: () => void
  onDragEnd: () => void
  onDragOver: () => void
  isDragging: boolean
}

export function ColumnHeader({
  column = {},
  moveColumn,
  updateWidth,
  onDragStart = () => {},
  onDragEnd = () => {},
  onDragOver = () => {},
  isDragging,
}: Props) {
  const { rundownId } = useParams()
  const layout = useRawColumnLayout(rundownId)
  const { patchLayout, setColumnWidth: setLayoutWidth } =
    useColumnLayoutActions(rundownId)
  const [touched, setTouched] = useState(false)
  const [dividerHover, setDividerHover] = useState(false)
  const addToast = useSetAtom(toastAtom)

  const { mutateAsync: deleteRundownColumn, isPending: isDeletingColumn } =
    useDeleteRundownColumn()
  const { mutateAsync: clearRundownPrivateColumn } =
    useClearRundownPrivateColumn()

  const isPrivateNotes = useIsPrivateNotesColumn({
    column,
  })
  const isEditor = useRundownPermission({
    permission: RundownPermission.EDIT,
  })
  const isPartialEditor = useRundownPermission({
    permission: RundownPermission.EDIT_PARTIAL,
  })

  const [searchParams] = useSearchParams()
  const editableColumns = useMemo(
    () => searchParams?.get('edit-columns')?.split(',') || [],
    [searchParams],
  )
  const isGuestEditableColumn =
    !isEditor && isPartialEditor && editableColumns.includes(column?.id)

  const isEditable = useMemo(() => {
    // Private notes are always editable
    if (isPrivateNotes) return true

    return isEditor
  }, [isEditor, isPrivateNotes])

  const [deleteColumnModal, renderDeleteColumnModal] =
    useModal(DeleteColumnModal)

  const handleToggleHide = useCallback(() => {
    const current = layout.hiddenColumns
    const next = current.includes(column.id)
      ? current.filter((id) => id !== column.id)
      : [...current, column.id]
    patchLayout({ hiddenColumns: next })
  }, [column.id, layout.hiddenColumns, patchLayout])

  const [dividerDragging, setDividerDragging] = useState(false)
  const [dividerPos, setDividerPos] = useState(
    column.width || DEFAULT_COLUMN_WIDTH,
  )

  const onDividerMove = useCallback(
    (event) => {
      event.preventDefault()
      if (!dividerDragging) return
      setTouched(true)
      const bounding = containerElement?.current.getBoundingClientRect()
      const newPos = event.clientX - bounding.left
      const MIN_POS = 100 // never smaller than 100px
      const MAX_POS = 800 // never bigger than 800px
      setDividerPos(_clamp(newPos, MIN_POS, MAX_POS))
    },
    [dividerDragging],
  )

  const onDividerUp = useCallback(
    (event) => {
      event.preventDefault()
      setDividerDragging(false)
      setTouched(false)
      const newWidth = containerElement?.current.getBoundingClientRect().width
      // Always write local
      setLayoutWidth(column.id, newWidth)
      // If write access (or private notes), also push to DB
      if (isEditable) {
        updateWidth(column.id, newWidth)
      }
    },
    [column.id, isEditable, setLayoutWidth, updateWidth],
  )

  const containerElement = useRef()
  const memoizedOnDividerMove = useCallback(onDividerMove, [onDividerMove])
  const memoizedOnDividerUp = useCallback(onDividerUp, [onDividerUp])

  function onDividerDown(event) {
    event.preventDefault()
    setDividerDragging(true)
  }

  const handleDeleteColumn = useCallback(async () => {
    try {
      if (isPrivateNotes) {
        await clearRundownPrivateColumn({
          rundownId,
          columnId: column.id,
        })
      } else {
        await deleteRundownColumn({
          rundownId,
          columnId: column.id,
        })
      }
    } finally {
      deleteColumnModal.hide()
    }
  }, [
    clearRundownPrivateColumn,
    column.id,
    deleteRundownColumn,
    deleteColumnModal,
    isPrivateNotes,
    rundownId,
  ])

  useEffect(() => {
    if (dividerDragging) {
      document.addEventListener('mousemove', memoizedOnDividerMove)
      document.addEventListener('mouseup', memoizedOnDividerUp)
    }
    return () => {
      document.removeEventListener('mousemove', memoizedOnDividerMove)
      document.removeEventListener('mouseup', memoizedOnDividerUp)
    }
  }, [dividerDragging, memoizedOnDividerMove, memoizedOnDividerUp])

  useEffect(() => {
    if (!touched) setDividerPos(column.width)
  }, [column.width, touched])

  return (
    <div
      ref={containerElement}
      id={column.id}
      draggable={true}
      onDragStart={dragStartInterceptor.bind(
        null,
        onDragStart.bind(null, column.id),
      )}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, column.id)}
      style={{
        width: `${dividerPos}px`,
      }}
      className="group relative"
    >
      {/* Header container */}
      <div className="relative flex items-center justify-between border-b-2 border-white/20">
        <div className="flex items-baseline gap-2 overflow-hidden min-w-0">
          {/* Left side - Column name input */}
          <EditableInput
            className={[
              'flex-1 h-6 leading-6 text-sm min-w-0 truncate text-ellipsis',
              isPrivateNotes ? 'text-yellow-300' : 'text-white',
            ].join(' ')}
            value={column.name}
            placeholder="Column name"
            onChange={(name) =>
              updateRundownColumn(rundownId, column.id, { name })
            }
            disabled={!isEditor || isPrivateNotes}
          />

          {isPrivateNotes && (
            <Tooltip>
              <TooltipTrigger className="text-yellow-300/50 text-xs">
                <FontAwesomeIcon icon={faCircleInfo} size="sm" />
              </TooltipTrigger>
              <TooltipContent>Only you can see this column.</TooltipContent>
            </Tooltip>
          )}

          {isGuestEditableColumn && (
            <Tooltip>
              <TooltipTrigger className="text-yellow-300/50 text-xs">
                <div className="px-1 text-yellow-300 text-xs pt-1">
                  Editable
                </div>
              </TooltipTrigger>
              <TooltipContent>You can edit this column</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Right side - Buttons */}
        <div className="flex items-center gap-1 flex-none group-hover:bg-black/50 group-hover:backdrop-blur-md rounded-t-md absolute top-0 right-0 pl-1">
          <Menu
            icon={faEllipsis}
            title="Edit column"
            className="!h-6 !w-6 !p-1 opacity-0 group-hover:opacity-30 hover:opacity-100 transition-opacity !bg-transparent"
          >
            {!isPrivateNotes && (
              <MenuItem
                label="Hide column for me"
                onClick={handleToggleHide}
                icon={faEyeSlash}
              />
            )}
            {!isPrivateNotes && (
              <Menu
                label="Column type..."
                icon={faTableColumns}
                disabled={!isEditor}
              >
                <MenuItem
                  label="Richtext"
                  onClick={() =>
                    updateRundownColumn(rundownId, column.id, {
                      type: 'richtext',
                    })
                  }
                  active={column.type === 'richtext'}
                />
                <MenuItem
                  label="Dropdown"
                  onClick={() =>
                    updateRundownColumn(rundownId, column.id, {
                      type: 'select',
                    })
                  }
                  active={column.type === 'select'}
                />
              </Menu>
            )}
            <MenuItem
              label="Add new column"
              onClick={() => addRundownColumn(rundownId, { after: column.id })}
              icon={faPlus}
              disabled={!isEditor}
            />
            <MenuItem
              label="Move left"
              onClick={() => moveColumn(column.id, 'left')}
              icon={faArrowLeft}
            />
            <MenuItem
              label="Move right"
              onClick={() => moveColumn(column.id, 'right')}
              icon={faArrowRight}
            />
            <MenuSeparator />
            <Menu icon={faCode} label="API" disabled={!isEditor}>
              <MenuItem
                icon={faCopy}
                label="Copy Column ID"
                onClick={() => {
                  copy(column.id)
                  return addToast({
                    title: `Column ID copied to the clipboard`,
                    body: `Column ID: ${column.id}`,
                    type: 'success',
                  })
                }}
              />
            </Menu>
            <MenuSeparator />
            <MenuItem
              label={isPrivateNotes ? 'Clear column' : 'Delete column'}
              onClick={deleteColumnModal.show}
              icon={faTrashAlt}
              disabled={!isEditable}
            />
          </Menu>

          <div
            data-draghandle
            title="Move column"
            className="h-6 w-4 opacity-0 group-hover:opacity-30 hover:opacity-100 px-1 cursor-grab transition-opacity"
          >
            <FontAwesomeIcon icon={faArrowsLeftRight} />
          </div>

          <div className="w-3" />

          <div
            onMouseDown={onDividerDown}
            onMouseEnter={() => setDividerHover(true)}
            onMouseLeave={() => setDividerHover(false)}
            className="absolute -right-2 h-6 w-4 opacity-0 group-hover:opacity-30 hover:opacity-100 px-1 cursor-col-resize transition-opacity"
          />
        </div>
      </div>

      {(dividerDragging || dividerHover) && !isDragging && (
        <span className="absolute -right-1 bg-gray-500 w-0.5 h-screen inline-block"></span>
      )}

      {renderDeleteColumnModal({
        column: column ?? {},
        onConfirm: handleDeleteColumn,
        loading: isDeletingColumn,
      })}
    </div>
  )
}
